import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useReducer, useRef, useCallback, useEffect } from 'react';
import { Container, Button, Card, Row, Col, Input, FormGroup, Label, CardBody, CardTitle } from 'reactstrap';
import DatePicker from "react-datepicker";

import { ApplicationState } from "../../store";
import * as AuthorizationStore from '../../store/Authorization';
import * as OrderSearchStore from '../../store/OrderSearch';

import GenericDropdownAsync from '../../components/controls/GenericDropdownAsync';
import Loader from '../../components/shared/Loader';

import ImpactPartnerService from '../../services/ImpactPartnerService';

import { ImpactPartner } from '../../models/ImpactPartner';
import { Order } from '../../models/Order';

import SearchOrderCard from './SearchOrderCard';
import SelectCartModal from './SelectCartModal';
import ProductsPropertiesChangedInfoModal from '../../components/shared/ProductsPropertiesChangedInfoModal';


type OrderSearchState = {
    selectedImpactPartner: ImpactPartner;
    orderNumber: string;
    createdDateFrom: Date;
    createdDateTo: Date;
    searchOnChange: boolean;
    cartName: string;
    openSelectCartDlg: boolean;
    reorderOrder: Order;
    openInfoChangedDialog: boolean;
};

const initialState: OrderSearchState = {
    selectedImpactPartner: undefined,
    orderNumber: '',
    createdDateFrom: undefined,
    createdDateTo: undefined,
    cartName: '',
    searchOnChange: false,
    openSelectCartDlg: false,
    reorderOrder: undefined,
    openInfoChangedDialog: false
}

function SearchOrderPage(props: {
    orderSearchStore: OrderSearchStore.IState,
    orderSearchActions: typeof OrderSearchStore.actionCreators,
    authorization: AuthorizationStore.IState;
}) {

    const [state, setState] = useReducer(
        (prevState: OrderSearchState, newState: Partial<OrderSearchState>) => ({ ...prevState, ...newState }),
        initialState
    );

    const { orderSearchStore, orderSearchActions } = props;
    const loader = useRef(null);
    const observer = useRef(null);

    const orderSearchApend = () => {
        if (orderSearchStore.postBack
            && !orderSearchStore.loading
            && orderSearchStore.totalCount > orderSearchStore.items.length) {
            let page = orderSearchStore.pageIndex + 1;
            orderSearchActions.orderSearch(state.selectedImpactPartner, state.orderNumber, state.createdDateFrom, state.createdDateTo, state.cartName, page, orderSearchStore.pageSize, true);
        }
    }

    const orderSearch = () => {
        orderSearchActions.orderSearch(state.selectedImpactPartner, state.orderNumber, state.createdDateFrom, state.createdDateTo, state.cartName, orderSearchStore.pageIndex, orderSearchStore.pageSize, false);
    };

    const orderSearchReset = () => {
        setState(initialState);

        orderSearchActions.orderSearchReset();
    };

    const lastDataElementRef = useCallback(node => {
        if (observer.current) {
            observer.current.disconnect();
        }

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) {
                orderSearchApend();
            }
        });

        if (node) {
            observer.current.observe(node);
        }
    }, [orderSearchStore]);

    const reorder = (order: Order) => {
        console.log("orderId", order.Id);
        
        setState({ reorderOrder: order });

        orderSearchActions.checkOrderProducts(order.PartnerId, order.Id);
    }

    useEffect(() => {
        if (orderSearchStore.reorderCartId !== undefined) {
            document.location.assign(`/cart?id=${orderSearchStore.reorderCartId}`);
        }
    }, [orderSearchStore])

    useEffect(() => {
        if (state.reorderOrder) {
            if (orderSearchStore.orderProductsChanged) {
                setState({ openInfoChangedDialog: true });
            }
            else {
                orderSearchActions.reorder(state.reorderOrder.PartnerId, state.reorderOrder.Id);
            }
        }

    }, [orderSearchStore.checkingOrderProduct])

    const buildCsvAndDownload = () => {
        props.orderSearchActions.loadAllAndDownloadCsv(state.selectedImpactPartner, state.orderNumber, state.createdDateFrom, state.createdDateTo, state.cartName);
    }

    const canShowExport = () => {
        if (props.orderSearchStore && props.orderSearchStore.totalCount && props.orderSearchStore.totalCount > 0) {
            return true;
        }
        return false;
    }

    return (<Container className="py-4 search-page">
        <Row>
            <Col>
                <h1>My Orders</h1>
            </Col>
        </Row>
        <Row>
            <Col>
                <Card body className="mt-3 pb-2">
                    <Row className="cart-form order-search-form align-items-end">
                        <Col xl="auto" md={6} sm={6}>
                            <FormGroup>
                                <Label for="ddImactPartner" className="form-label">
                                    Impact Partner
                                </Label>
                                <GenericDropdownAsync
                                    loadDataSource={() => ImpactPartnerService.list()}
                                    getId={x => x.Id}
                                    renderItem={x => x.Name}
                                    renderSelectedItem={x => x ? x.Name : 'Select Impact Partner'}
                                    allowNullSelection={true}
                                    nullSelectionText='Select Impact Partner'
                                    selectedValue={state.selectedImpactPartner ? state.selectedImpactPartner.Id : undefined}
                                    onSelectedItemChanged={item => setState({ selectedImpactPartner: item })}
                                />

                            </FormGroup>
                        </Col>
                        <Col xl="auto" md={6} sm={6}>
                            <FormGroup>
                                <Label for="txtOrderNumber" className="form-label">
                                    Order #
                                </Label>
                                <Input
                                    id="txtOrderNumber"
                                    name="orderNumber"
                                    placeholder="Order Number"
                                    type="text"
                                    value={state.orderNumber}
                                    onChange={(e) => { setState({ orderNumber: e.target.value }); }}
                                />
                            </FormGroup>
                        </Col>
                        <Col xl="auto" md={3} sm={6} className="col-6 datepicker-cell">
                            <FormGroup>
                                <Label for="dpCreatedDateFrom" className="form-label">
                                    Created From
                                </Label>
                                <DatePicker
                                    id="dpCreatedDateFrom"
                                    placeholderText="dd/mm/yyyy"
                                    selected={state.createdDateFrom}
                                    onChange={(date) => {
                                        setState({ createdDateFrom: date });
                                    }}
                                    dateFormat={"dd/MM/yyyy"}
                                    className="form-control" 
                                    showIcon
                                />
                            </FormGroup>
                        </Col>
                        <Col xl="auto" md={3} sm={6} className="col-6  datepicker-cell">
                            <FormGroup>
                                <Label for="dpCreatedDateTo" className="form-label">
                                    Created To
                                </Label>
                                <DatePicker
                                    id="dpCreatedDateTo"
                                    placeholderText="dd/mm/yyyy"
                                    selected={state.createdDateTo}
                                    onChange={(date) => {
                                        setState({ createdDateTo: date });
                                    }}
                                    dateFormat={"dd/MM/yyyy"}
                                    className="form-control"
                                    showIcon
                                />
                            </FormGroup>
                        </Col>
                        <Col xl="auto" md={6} sm={6}>
                            <FormGroup>
                                <Label for="txtCartName" className="form-label">
                                    Cart Name
                                </Label>
                                <Input
                                    id="txtCartName"
                                    name="cartName"
                                    placeholder="Cart Name"
                                    type="text"
                                    value={state.cartName}
                                    onChange={(e) => { setState({ cartName: e.target.value }); }}
                                />
                            </FormGroup>
                        </Col>

                        <Col className="search-btn-col">
                            <div className="d-flex buttons-group">
                                <div className="form-group">
                                    <label className="d-md-block d-sm-none">&nbsp;</label>
                                    <Button
                                        color="primary"
                                        className="rounded-0 px-4 text-uppercase"
                                        onClick={orderSearch}
                                    >
                                        Search Orders
                                    </Button>
                                </div>
                                <div className="form-group">
                                    <label className="d-md-block d-sm-none">&nbsp;</label>
                                    <Button
                                        color="clear"
                                        onClick={orderSearchReset}                                        
                                    >
                                        Reset Search
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card>
            </Col>
        </Row>
        <Row className="py-4">
            <Col>
                {props.orderSearchStore.totalCount > -1 &&

                    <div className="d-flex justify-content-between align-items-center">
                        <div className="pb-2 mb-3">
                            <span>Total Orders: </span><b>{new Intl.NumberFormat('en-US').format(props.orderSearchStore.totalCount)}</b>
                        </div>
                        {canShowExport() && <div className="export text-uppercase">
                            <a href="#" onClick={(e) => {
                                e.preventDefault();
                                buildCsvAndDownload();
                            }}>
                                Export CSV
                            </a>
                            {props.orderSearchStore.isLoadingForCsv && <Loader />}
                        </div>}
                    </div>
                }
                {(orderSearchStore.totalCount > -1
                    && orderSearchStore.items && orderSearchStore.items.length > 0) &&
                    <>{orderSearchStore.items.map((order) => (
                        <Card className="mb-3" key={order.Id}>
                            <CardBody>
                                <CardTitle tag="h5">
                                    {order.PartnerName}
                                </CardTitle>
                                <div className="order-card-wrapper">
                                    <SearchOrderCard order={order} reorder={reorder} reorderInProgress={orderSearchStore.reorderInProgress} />
                                </div>
                            </CardBody>
                        </Card>
                       
                    ))}</>
                }
                <div ref={lastDataElementRef}></div>
                <div ref={loader}>{orderSearchStore.loading && <Loader />}</div>
                <div ref={loader}>{orderSearchStore.reorderInProgress && <Loader />}</div>
            </Col>
        </Row>
        <SelectCartModal open={state.openSelectCartDlg}
            userId={props.authorization.currentUser ? props.authorization.currentUser.UserId : undefined}
            addCreateCartOption={false}
            cartToExclude={undefined}
            onCancel={() => setState({
                openSelectCartDlg: false
            })}
            onSave={(cart) => {
                //orderSearchActions.reorder(cart, state.reorderOrder.PartnerId, state.reorderOrder.Id);
                //props.cartActions.add(cart.CartId, cart.CartName, {
                //    CartId: cart.CartId,
                //    ProductShortCode: props.model.ShortCode,
                //    ProductNumber: props.model.ProductNumber,
                //    Description: props.model.Description,
                //    Remanufactured: props.model.Remanufactured,
                //    PartnerId: state.productToBuy.PartnerId,
                //    PartnerName: state.productToBuy.PartnerName,
                //    PartnerPartNumber: state.productToBuy.ImpactPartnerPartNumber,
                //    PricePerUnit: state.productToBuy.PricePerUnit,
                //    ApprovedBy: approvedBy,
                //    Amount: amount
                //})

                setState({ reorderOrder: undefined, openSelectCartDlg: false });
            }}
        />

        <ProductsPropertiesChangedInfoModal open={state.openInfoChangedDialog}
            onClose={() => {
                setState({
                    openInfoChangedDialog: false
                });

                orderSearchActions.reorder(state.reorderOrder.PartnerId, state.reorderOrder.Id);
            }}
        />
    </Container>);
}

export default connect((state: ApplicationState) => ({
    orderSearchStore: state.orderSearch ? state.orderSearch : OrderSearchStore.unloadedState,
    authorization: state.authorization
}),
    (dispatch, getState) => ({
        orderSearchActions: bindActionCreators(OrderSearchStore.actionCreators, dispatch)
    }))(SearchOrderPage);